<script lang="ts" setup>
import { CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS } from '@/core/collections/contracts-api/customers/team-tax-discounts/team-tax-discount.endpoints';
import { CustomerTeamTaxDiscount } from '@/core/collections/contracts-api/customers/team-tax-discounts/team-tax-discount.interface';
import {
   getDefaultTeamTaxDiscountData,
   useTeamTaxDiscountSchema,
} from '@/core/collections/contracts-api/customers/team-tax-discounts/team-tax-discount.schema';
import { TEAM_TAX_DISCOUNT_TYPE_ENDPOINTS } from '@/core/collections/contracts-api/team-tax-discount-types/team-tax-discount-type.endpoints';
import { TeamTaxDiscountType } from '@/core/collections/contracts-api/team-tax-discount-types/team-tax-discount-type.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import GenericList from '@/core/components/templates/generic-list/GenericList.vue';
import { ApiResource } from '@/core/models/ApiResource.interface';
import { NxtGenericFormModal } from '@nxtpeople/nxt-styles';
import ContractAndPerformanceDataTaxDiscountsForm from './ContractAndPerformanceDataTaxDiscountsForm.vue';

//#region Globals
const { query } = useRoute();
const customerId = useRoute().params.id;
const { t } = useI18n();
const { TEAM_TAX_DISCOUNT_SCHEMA } = useTeamTaxDiscountSchema();
const { data: teamTaxDiscountTypes } = useFetch<ApiResource<Array<TeamTaxDiscountType>>>(TEAM_TAX_DISCOUNT_TYPE_ENDPOINTS.GET_ALL(), {
   ...cacheFirstConfig,
});
//#endregion

//#endregion

//#region Get Tax Discounts
const { data, isLoading, execute } = useFetch<ApiResource<Array<CustomerTeamTaxDiscount>>>(
   CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS.GET_ALL(customerId),
   {
      immediate: false,
   },
);

useQueryWatcher('ContractAndPerformanceDataTaxDiscounts', (query) => execute({ params: query }));
//#endregion

//#region Create Tax Discount
const { execute: create, isLoading: creating } = usePost<Partial<CustomerTeamTaxDiscount>>(
   CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS.POST(customerId),
);

async function createTeamTaxDiscount(customerTeamTaxDiscount: Partial<CustomerTeamTaxDiscount>, closeCreateItemModal: () => void) {
   await create(CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS.POST(customerId), { data: customerTeamTaxDiscount });
   await execute({ params: query });
   closeCreateItemModal();
}
//#endregion

//#region Change Tax Discount
const { execute: change, isLoading: changing } = usePut<Partial<CustomerTeamTaxDiscount>>(
   CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS.PUT(customerId),
);

async function changeTeamTaxDiscount(customerTeamTaxDiscount: Partial<CustomerTeamTaxDiscount>, closeChangeItemModal: () => void) {
   await change(CUSTOMER_TEAM_TAX_DISCOUNT_ENDPOINTS.PUT(customerId, customerTeamTaxDiscount.id), {
      data: {
         ...customerTeamTaxDiscount,
         bis_monthly_percentages: customerTeamTaxDiscount.bis_monthly_percentages?.map((b) => ({
            ...b,
            value: (+(b.value ?? 0) / 100).toFixed(4),
         })),
      },
   });
   await execute({ params: query });
   closeChangeItemModal();
}
</script>

<template>
   <GenericList :name="'tax-discoutns'" :list-items="data?.data" :mode="'light'" :meta="data?.meta" :loading="isLoading">
      <template #filters="{ query, filter }">
         <Dropdown
            :model-value="query.team_tax_discount_type_id"
            :name="'filter_team_tax_discount_type_id'"
            :mode="'default'"
            :options="teamTaxDiscountTypes?.data ?? []"
            :placeholder="$t('general.team-tax-discount-types')"
            @update:model-value="filter({ key: 'team_tax_discount_type_id', value: $event })"
         />
      </template>

      <template #headings>
         <th :style="{ minWidth: '20rem' }">{{ $t('general.team-tax-discount-types') }}</th>
         <th :style="{ minWidth: '10rem' }">{{ $t('generic.dates.from') }}</th>
         <th :style="{ minWidth: '10rem' }">{{ $t('generic.dates.till') }}</th>
         <th :style="{ minWidth: '30rem' }">{{ $t('customers.amount-of-bis-monthly-percentages') }}</th>
         <th :style="{ width: '100%' }">{{ $t('customers.this-months-bis-percentage') }}</th>
      </template>

      <template #listItem="{ item }">
         <td>{{ teamTaxDiscountTypes?.data?.find((ttdt) => ttdt.id === item.team_tax_discount_type_id)?.name?.[$i18n.locale] ?? '-' }}</td>
         <td v-date="item.from"></td>
         <td v-date="item.till"></td>
         <td>
            <span v-if="item.bis_monthly_percentages">
               {{ item.bis_monthly_percentages.length }}
            </span>
            <span v-else>-</span>
         </td>
         <td>
            <span v-if="item.bis_monthly_percentages">
               {{
                  item.bis_monthly_percentages.find((b: any) => $dayjs(b.month_start_date).month() === $dayjs().month())?.percentage ?? '-'
               }}
            </span>
            <span v-else>-</span>
         </td>
      </template>

      <template v-if="$auth('update', $store.state.permission?.permissions?.customers)" #createModal="{ closeCreateItemModal }">
         <NxtGenericFormModal
            :data="getDefaultTeamTaxDiscountData()"
            :modal-type="'create'"
            :data-type="t('customers.teams-with-tax-discounts')"
            :loading="creating"
            :schema="TEAM_TAX_DISCOUNT_SCHEMA"
            @close="closeCreateItemModal"
            @save="(data) => createTeamTaxDiscount(data, closeCreateItemModal)"
         >
            <template #default="params">
               <ContractAndPerformanceDataTaxDiscountsForm v-bind="params" />
            </template>
         </NxtGenericFormModal>
      </template>

      <template #changeModal="{ closeChangeItemModal, item }">
         <NxtGenericFormModal
            :data="{
               ...item,
               bis_monthly_percentages: item.bis_monthly_percentages?.map((b: any) => ({
                  ...b,
                  value: +(b.value * 100),
               })),
            }"
            :modal-type="'change'"
            :data-type="t('customers.teams-with-tax-discounts')"
            :loading="changing"
            :schema="TEAM_TAX_DISCOUNT_SCHEMA"
            @close="closeChangeItemModal"
            @save="(data) => changeTeamTaxDiscount(data, closeChangeItemModal)"
         >
            <template #default="params">
               <ContractAndPerformanceDataTaxDiscountsForm v-bind="params" :initial-tax-discount="{ ...item }" />
            </template>
         </NxtGenericFormModal>
      </template>
   </GenericList>
</template>
